import React from "react";
import useAIEnhancerService from "./service";
import { AITextEnhancementActionItem, AITextEnhancerProps } from "./type";
import Button from "../button";
import Icon from "assets/icons";
import ModalPopup from "../modalPopup";
import { useCopies } from "shared/hooks/useCopies";
import { TextEnhancementFormField } from "./enum";
import FormContainer from "shared/form";
import ButtonType from "shared/enums/ButtonTypes";
import TextEnhancementType from "shared/enums/TextEnhancementType";
import FormTextAreaFieldCustom from "shared/form/fields/textAreaField/formTextAreaFieldCustom";
import { ComposedIcons } from "assets/icons/icons.compose";
import Size from "shared/enums/Size";
import AlertModal from "../alertModal";
import Variant from "shared/enums/Variant";

export default function AITextEnhancer(props: AITextEnhancerProps) {
  const copies = useCopies();
  const service = useAIEnhancerService(props);

  return (
    <React.Fragment>
      {/* todo:rr check onclick function  */}
      <Button.Custom
        disabled={(props.value?.trim().length ?? 0) < props.minLengthToEnableAIAssist}
        className="aiTextEnhanceBtn"
        tooltipContent={
          (props.value?.trim().length ?? 0) < props.minLengthToEnableAIAssist
            ? copies.get("ai.assist.min.length.not.met", { 0: props.minLengthToEnableAIAssist })
            : undefined
        }>
        <span onClick={service.toggleDisplayModal} className="aiTextEnhanceBtnWrapper">
          {(props.value?.trim().length ?? 0) < props.minLengthToEnableAIAssist ? (
            <Icon.AIIconStatic />
          ) : (
            <ComposedIcons.AIIcon />
          )}{" "}
          {copies.get("ai.text.assist")}
        </span>
      </Button.Custom>
      {service.invalidText && (
        <AlertModal
          handleConfirm={() => service.setInvalidText(undefined)}
          message={service.invalidText}
          primaryButtonText={copies.get("ok")}
          primaryButtonVariant={Variant.PRIMARY}
        />
      )}
      {service.displayModal && (
        <ModalPopup onModalPopupClose={service.toggleDisplayModal} size={Size.LARGE} className="aiTextEnhanceModal">
          <FormContainer
            initialValues={service.initialValues}
            onSubmit={service.submitHandler}
            validationSchema={service.validationSchema}
            innerRef={service.formRef}
            enableReinitialize>
            <FormContainer.Form>
              <div className="aiTextEnhanceWrapper">
                <div className="aiTextEnhanceLeft">
                  <div className="aiTextEnhanceHeader">
                    <div className="aiTextEnhanceHeaderLabel">{copies.get("text.original")}</div>
                  </div>
                  <div className="aiTextEnhanceBody">{props.value}</div>
                </div>
                <div className="aiTextEnhanceSeperator">
                  <Icon.DoubleArrow />
                </div>
                <div className="aiTextEnhanceRight">
                  <div className="aiTextEnhanceHeader">
                    <div className="aiTextEnhanceHeaderLabel">
                      <ComposedIcons.AIIcon /> {copies.get("text.enhance")}
                    </div>
                  </div>
                  <div className="aiTextEnhanceBody">
                    <div className="aiTextEnhanceActions">
                      {/* condition for displaying all ai actions upfront */}
                      {!service.generatedText && !service.isGeneratingText && (
                        <React.Fragment>
                          <div className="aiTextEnhanceActions_label">
                            {copies.get("select.text.enhancement.option")}
                          </div>
                          <DisplayTextEnhancementActions
                            aiTextActions={service.aiTextActions}
                            generateTextActionHandler={service.generateTextActionHandler}
                            displayActionsUpfront={true}
                            lastActionTaken={service.lastActionTaken}
                          />
                        </React.Fragment>
                      )}
                      {/* condition for displaying the shimmer effect when generating text */}
                      {service.isGeneratingText && (
                        <ShimmerEffectForText
                          aiTextActions={service.aiTextActions}
                          generateTextActionHandler={service.generateTextActionHandler}
                          lastActionTaken={service.lastActionTaken}
                        />
                      )}
                      {/* condition for displaying the generated text along with further actions */}
                      {service.generatedText && !service.isGeneratingText && (
                        <FormTextAreaFieldCustom
                          className="slideDownAnimation"
                          label={""}
                          name={TextEnhancementFormField.ENHANCED_TEXT}
                          maxCount={props.maxLength}
                          customComponent={
                            <DisplayTextEnhancementActions
                              aiTextActions={service.aiTextActions}
                              generateTextActionHandler={service.generateTextActionHandler}
                              displayActionsUpfront={false}
                              lastActionTaken={service.lastActionTaken}
                            />
                          }
                        />
                      )}
                    </div>
                  </div>
                  <div className="aiTextEnhanceFooter">
                    <div className="aiTextEnhanceFooter_msg">
                      <Icon.InfoCircle size={15} top={-1} /> {copies.get("text.enhancement.info")}
                    </div>

                    <div className="aiTextEnhanceFooter_action">
                      <Button.Save
                        label={copies.get("ai.text.action.insert")}
                        icon={<Icon.AddCircle />}
                        type={ButtonType.SUBMIT}
                        disabled={service.isGeneratingText || !service.generatedText}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </FormContainer.Form>
          </FormContainer>
        </ModalPopup>
      )}
    </React.Fragment>
  );
}

function ShimmerEffectForText(props: {
  aiTextActions: Array<AITextEnhancementActionItem>;
  generateTextActionHandler: (action?: TextEnhancementType) => void;
  lastActionTaken?: TextEnhancementType;
}) {
  return (
    <div className="shimmerEffectForText slideDownAnimation">
      <div className="shimmerEffectForText_wrapper ">
        <ComposedIcons.AILoader />
      </div>
    </div>
  );
}

function DisplayTextEnhancementActions(props: {
  aiTextActions: Array<AITextEnhancementActionItem>;
  generateTextActionHandler: (action?: TextEnhancementType) => void;
  displayActionsUpfront: boolean;
  disabled?: boolean;
  lastActionTaken?: TextEnhancementType;
}) {
  const copies = useCopies();
  return (
    <React.Fragment>
      <div className={props.displayActionsUpfront ? "aiTextEnhanceActionsUpfront" : "aiTextEnhanceActionsInner"}>
        {props.aiTextActions.map((o: AITextEnhancementActionItem) => (
          <div key={o.actionKey} className="aiTextEnhanceActions_item">
            <Button
              className="aiTextEnhanceActions_btn"
              label={copies.get(o.copyKey)}
              onClick={() => props.generateTextActionHandler(o.actionKey)}
              disabled={props.disabled || o.actionKey === props.lastActionTaken}
              isOutline={o.actionKey !== props.lastActionTaken}
            />
            {props.displayActionsUpfront && (
              <span className="aiTextEnhanceActions_btnLabel">{copies.get(o.infoCopyKey)}</span>
            )}
          </div>
        ))}
        {!props.displayActionsUpfront && (
          <div className="aiTextEnhanceActions_item ">
            <Button.Link
              className="aiTextEnhanceActions_btnRecreate"
              label={copies.get("ai.text.action.recreate")}
              icon={<Icon.Refresh size={17} />}
              onClick={() => props.generateTextActionHandler()}
              disabled={props.disabled}
            />
          </div>
        )}
      </div>
    </React.Fragment>
  );
}
